import React from 'react';
import { Formik, Form, Field, FieldArray } from 'formik';
import { Switch, TextField } from 'formik-material-ui';
import { makeStyles } from '@material-ui/styles';
import { useSnackbar } from 'notistack';
import Auth from '../../util/Auth';
import moment from 'moment';
import { Button, FormLabel, InputLabel, MenuItem, TextField as MaterialUITextField } from '@material-ui/core';
import MaterialTable from 'material-table';
import Select from 'react-select';
import CreateableSelect from 'react-select/creatable';
import Link from 'react-router-dom/Link';
import PERMISSIONS_LIST from '../../util/RolesEnum';
import DeleteOtherLeaseModal from './DeleteOtherLeaseModal';
import { useQuery } from '@apollo/client';
import { GET_OPERATOR_DETAILS } from '../../components/otherleases/otherleaseQueries';

const hasPermissions = Auth.hasRole(PERMISSIONS_LIST.WRITE.WRITE_OTHER_LEASE);
const DetailPageStyles = makeStyles((theme) => ({
    paper: {
        padding: 25,
        marginBottom: '1rem',
        marginTop: '1rem',
    },
    TextField: {
        display: 'block',
        marginTop: 10,
        marginBottom: 10,
        paddingRight: '1em',
    },
    form: {
        marginTop: '20px',
    },
    title: {
        color: '#1976d2',
        fontSize: '1.75rem',
        textAlign: 'center',
        textDecoration: 'underline',
    },
    gdone: {
        display: 'grid',
        gridTemplateColumns: '25% 1fr',
        gap: '1rem',
    },
    left: {
        display: 'grid',
        gridTemplateRows: 'repeat(5, auto)',
    },
    right: {
        display: 'grid',
        gridTemplateRows: 'repeat (3, 1fr)',
        // gap: '1rem',
        marginLeft: '30px'
    },
    notes: {
        width: '100%',
        height: '100%',
        marginTop: '1rem',
    },
    buttons: {
        display: hasPermissions ? '' : 'none',
        marginTop: 20,
    },
    totalDisplay: {
        textAlign: 'right',
        marginRight: '10px',
        padding: '10px',
        fontWeight: 'bold'
    },
}));
const OtherLeaseDetailPage = ({
                                  modalOpen,
                                  setModalOpen,
                                  OtherLease,
                                  tracts,
                                  history,
                                  operators: initialOperators,
                                  deleteOL,
                                  placeLeaseOn,
                                  removeLease,
                                  addOperator,
                                  removeOperator,
                                  addOtherLeasePayment,
                                  deleteOtherLeasePayment,
                                  updateOtherLeasePayment,
                                  EditOL,
                              }) => {
    const classes = DetailPageStyles();
    const { enqueueSnackbar } = useSnackbar();
    // Fetch full operator details dynamically
    const { data: operatorData, loading, error: queryError, refetch } = useQuery(GET_OPERATOR_DETAILS, {
        variables: { id: OtherLease.operator?.id || '' },
        skip: !OtherLease.operator?.id,
    });

    const initialLeaseValues = {
        ...OtherLease,
        // dateOfLease: !OtherLease.dateOfLease
        //     ? 'None'
        //     : moment(OtherLease.dateOfLease, 'MM/DD/YYYY').format('MM/DD/YYYY'),
        dateOfLease: !OtherLease.dateOfLease
            ? 'None'
            : moment(OtherLease.dateOfLease, 'YYYY-MM-DD').format('YYYY-MM-DD'),
        dateOfExpiration: !OtherLease.dateOfExpiration
            ? 'None'
            : moment(OtherLease.dateOfExpiration, 'YYYY-MM-DD').format('YYYY-MM-DD'),
        datePaid: moment(OtherLease.datePaid, 'YYYY-MM-DD').format('YYYY-MM-DD'),
        address: OtherLease.operator?.address1 || '',
        city: OtherLease.operator?.city || '',
        state: OtherLease.operator?.state || '',
        zip: OtherLease.operator?.zip || '',
        payments: OtherLease.payments ? OtherLease.payments.map(payment => ({
            ...payment,
            date: payment.date || '',
            paymentFrom: payment.paymentFrom || '',
            entity: payment.entity || '',
            paymentType: payment.paymentType || ''
        })) : []
    };

    const handleLesseeChange = (option, setFieldValue) => {
        setFieldValue('lessee', option.name);
        setFieldValue('operatorID', option.id);
        // Also update projectName with the same value as lessee (operator)
        setFieldValue('projectName', option.name);
        refetch({ id: option.id }).then(({ data }) => {
            const selectedOperator = data?.Operator?.[0] || initialOperators.find((op) => op.id === option.id);
            if (selectedOperator) {
                setFieldValue('address', selectedOperator.address1 || '');
                setFieldValue('city', selectedOperator.city || '');
                setFieldValue('state', selectedOperator.state || '');
                setFieldValue('zip', selectedOperator.zip || '');
            } else {
                setFieldValue('address', '');
                setFieldValue('city', '');
                setFieldValue('state', '');
                setFieldValue('zip', '');
            }
        }).catch((err) => {
            console.error('Error refetching operator:', err);
            setFieldValue('address', '');
            setFieldValue('city', '');
            setFieldValue('state', '');
            setFieldValue('zip', '');
        });
    };

    if (loading) return <div>Loading...</div>;
    if (queryError) return <div>Error: {queryError.message}</div>;

    const currencyFormat = (locale = 'en-US', style = 'currency', currency = 'USD') => {
        return new Intl.NumberFormat(locale, {
            style,
            currency,
        });
    };

    const editableRow = Object.assign({}, OtherLease)

    editableRow.dateOfLease = !editableRow.dateOfLease ? 'None' : moment(editableRow.dateOfLease).format('MM/DD/YYYY');
    editableRow.dateOfExpiration = !editableRow.dateOfExpiration ? 'None' : moment(editableRow.dateOfExpiration).format('YYYY-MM-DD');
    editableRow.datePaid = moment(editableRow.datePaid).format('YYYY-MM-DD');

    return (
        <Formik
            initialValues={initialLeaseValues}
            onSubmit={(values, actions) => {
                if (!values.operator) {
                    addOperator({
                        variables: {
                            from: { id: values.operatorID },
                            to: { id: OtherLease.id },
                        },
                    })
                        .then(({ data: { AddOperatorOtherLeases } }) => {
                            actions.setFieldValue('operator', AddOperatorOtherLeases.from);
                            // Update projectName when Lessee (operator) is added
                            actions.setFieldValue('projectName', AddOperatorOtherLeases.from.name);
                        })
                        .catch((error) => console.log(error));
                }

                if (values.lessee !== OtherLease.lessee && values.operator) {
                    removeOperator({
                        variables: {
                            from: { id: OtherLease.operator.id },
                            to: { id: OtherLease.id },
                        },
                    })
                        .then(
                            addOperator({
                                variables: {
                                    from: {id: values.operatorID},
                                    to: {id: OtherLease.id},
                                },
                            }).then(({data: {AddOperatorOtherLeases}}) => {
                                actions.setFieldValue('operator', AddOperatorOtherLeases.from);
                                // Update projectName when Lessee (operator) is changed, keep projectName in sync with Lessee
                                actions.setFieldValue('projectName', AddOperatorOtherLeases.from.name);
                            })
                        )
                        .catch((error) => console.log(error));
                }

                return EditOL({
                    variables: { ...values },
                })
                    .then(({ data: { UpdateROWUpdateOtherLease } }) => {
                        enqueueSnackbar('Lease successfully edited', { variant: 'success' });
                        actions.setSubmitting(false);
                    })
                    .catch((err) => {
                        enqueueSnackbar(err.toString(), { variant: 'error' });
                        actions.setSubmitting(false);
                    });
            }}
        >
            {(props) => {
                const totalAcres = props.values.locatedOn
                    .reduce((sum, land) => sum + (parseFloat(land.acreage) || 0), 0)
                    .toFixed(2);

                return (
                    <Form id="editLease">
                        <div className={classes.gdone}>
                            <div className={classes.left}>
                                <div style={{ width: 300 }}>
                                    <InputLabel>Lessee</InputLabel>
                                    <Field name='lessee' id='lessee'>
                                        {({ field, form }) => (
                                            <CreateableSelect
                                                defaultValue={{
                                                    name: OtherLease.operator ? field.value : '...Select',
                                                    id: OtherLease.operator ? props.values.operator.id : field.value,
                                                    address1: OtherLease.operator?.address1 || '',
                                                    city: OtherLease.operator?.city || '',
                                                    state: OtherLease.operator?.state || '',
                                                    zip: OtherLease.operator?.zip || '',
                                                }}
                                                options={initialOperators}
                                                getOptionLabel={(option) => option.name}
                                                getOptionValue={(option) => option.id}
                                                onChange={(option) => handleLesseeChange(option, form.setFieldValue)}
                                                styles={{
                                                    option: (provided, state) => ({ ...provided, fontFamily: 'Roboto' }),
                                                    singleValue: (provided, state) => ({ ...provided, fontFamily: 'Roboto' }),
                                                }}
                                            />
                                        )}
                                    </Field>
                                </div>
                                <Field
                                    name='address'
                                    label='Address'
                                    placeholder='Address'
                                    component={TextField}
                                    className={classes.TextField}
                                    disabled
                                />
                                <Field
                                    name='city'
                                    label='City'
                                    placeholder='City'
                                    component={TextField}
                                    className={classes.TextField}
                                    disabled
                                />
                                <Field
                                    name='state'
                                    label='State'
                                    placeholder='State'
                                    component={TextField}
                                    className={classes.TextField}
                                    disabled
                                />
                                <Field
                                    name='zip'
                                    label='Zip'
                                    placeholder='Zip'
                                    component={TextField}
                                    className={classes.TextField}
                                    disabled
                                />
                                <Field
                                    name='termOfLease'
                                    placeholder='Lease Term'
                                    component={TextField}
                                    label='Lease Term'
                                    className={classes.TextField}
                                />
                                <Field
                                    name='dateOfLease'
                                    placeholder='MM-DD-YYYY'
                                    type='date'
                                    component={TextField}
                                    label='Date of Lease'
                                    className={classes.TextField}
                                    InputLabelProps={{shrink: true}}
                                />
                                <Field
                                    name='dateOfExpiration'
                                    label='Lease Termination'
                                    placeholder='MM-DD-YYYY'
                                    type='date'
                                    component={TextField}
                                    className={classes.TextField}
                                    InputLabelProps={{shrink: true}}
                                />
                            </div>
                            <div className={classes.right}>
                                <div style={{ display: 'flex' }}>
                                <Field
                                    name='type'
                                    margin='normal'
                                    select
                                    label='Type'
                                    component={TextField}
                                    className={classes.TextField}
                                    InputLabelProps={{shrink: true}}
                                >
                                    <MenuItem value='GRAZING'>GRAZING</MenuItem>
                                    <MenuItem value='FARMING'>FARMING</MenuItem>
                                </Field>
                                <Field
                                    name='status'
                                    margin='normal'
                                    select
                                    label='Status'
                                    component={TextField}
                                    className={classes.TextField}
                                    InputLabelProps={{shrink: true}}
                                >
                                    <MenuItem value='Active'>Active</MenuItem>
                                    <MenuItem value='Inactive'>Inactive</MenuItem>
                                </Field>
                                </div>
                                <FieldArray name='locatedOn'>
                                    {({push, remove}) => {
                                        const columns = [
                                            {
                                                title: 'Land ID',
                                                field: 'id',
                                                editComponent: ({onChange}) => (
                                                    <Select
                                                        options={tracts}
                                                        getOptionLabel={(option) => option.name}
                                                        getOptionValue={(option) => option.id}
                                                        onChange={onChange}
                                                        styles={{
                                                            menu: (provided) => ({...provided, width: 150}),
                                                            menuList: (provided) => ({...provided, width: 150}),
                                                        }}
                                                    />
                                                ),
                                            },
                                            // {
                                            //     title: 'Family ID',
                                            //     field: 'familyID',
                                            //     editable: 'never',
                                            // },
                                            // {
                                            //     title: 'State',
                                            //     field: 'state',
                                            //     editable: 'never',
                                            // },
                                            // {
                                            //     title: 'County',
                                            //     field: 'county',
                                            //     editable: 'never',
                                            // },
                                            {
                                                title: 'ACRES',
                                                field: 'acreage',
                                                editable: 'never',
                                            },
                                        ];

                                        return (
                                            <MaterialTable
                                                columns={columns}
                                                data={!props.values.locatedOn ? [] : props.values.locatedOn.map((o) => ({...o}))}
                                                title='Lands'
                                                options={{
                                                    headerStyle: {
                                                        backgroundColor: '#1976d2',
                                                        color: '#FFF',
                                                        textAlign: 'left',
                                                        width: 'auto',
                                                    },
                                                    cellStyle: {
                                                        maxWidth: 0,
                                                        overflowX: 'auto',
                                                    },
                                                    search: false,
                                                    paging: false,
                                                    toolbarButtonAlignment: 'left',
                                                    addRowPosition: 'first',
                                                }}
                                                editable={{
                                                    onRowAdd: (newData) => {
                                                        const tractID = newData.id.id;
                                                        props.setSubmitting(true);
                                                        return placeLeaseOn({
                                                            variables: {
                                                                from: {id: OtherLease.id},
                                                                to: {id: tractID},
                                                            },
                                                        }).then((data) => {
                                                            // Update land table with mutation response
                                                            push(data.data.AddOtherLeaseLocatedOn.to);
                                                            props.setSubmitting(false);
                                                        }).catch((error) => {
                                                            props.setSubmitting(false);
                                                            console.error('Error adding land:', error);
                                                        });
                                                    },
                                                    onRowDelete: (oldData) => {
                                                        props.setSubmitting(true);
                                                        return removeLease({
                                                            variables: {
                                                                from: {id: OtherLease.id},
                                                                to: {id: oldData.id},
                                                            },
                                                        }).then((data) => {
                                                            props.setSubmitting(false);
                                                            remove(oldData.tableData.id);
                                                        });
                                                    },
                                                }}
                                            />
                                        );
                                    }}
                                </FieldArray>
                                <div className={classes.totalDisplay}>
                                    Total Acres: {totalAcres}
                                </div>
                                <div style={{marginTop: 20}}>
                                    <FieldArray name='payments'>
                                        {({push, remove, insert}) => {
                                            const columns = [
                                                {
                                                    title: 'Date of Check',
                                                    field: 'date',
                                                },
                                                {
                                                    title: 'Payment From',
                                                    field: 'paymentFrom',
                                                },
                                                {
                                                    title: 'Entity Paid',
                                                    field: 'entity',
                                                },
                                                {
                                                    title: 'Type of Payment',
                                                    field: 'paymentType',
                                                },
                                                {
                                                    title: 'Amount',
                                                    field: 'amount',
                                                },
                                            ];
                                            return (
                                                <MaterialTable
                                                    columns={columns}
                                                    data={!props.values.payments ? [] : props.values.payments.map(p => ({...p}))}
                                                    title={'Payments'}
                                                    options={{
                                                        headerStyle: {
                                                            backgroundColor: '#1976d2',
                                                            color: '#FFF',
                                                        },
                                                        rowStyle: (rowData) => ({
                                                            backgroundColor: rowData.current === 'Yes' ? '#EEE' : '#FFF',
                                                        }),
                                                        cellStyle: {
                                                            maxWidth: 0,
                                                            overflowX: 'auto',
                                                        },
                                                        paging: false,
                                                        exportButton: true,
                                                        toolbarButtonAlignment: 'left',
                                                        searchFieldAlignment: 'left',
                                                    }}
                                                    editable={{
                                                        onRowAdd: (newData) => {
                                                            props.setSubmitting(true);
                                                            return addOtherLeasePayment({
                                                                variables: {
                                                                    OtherLeaseID: editableRow.id,
                                                                    info: {
                                                                        ...newData,
                                                                        amount: currencyFormat().format(newData.amount),
                                                                    },
                                                                },
                                                            })
                                                                .then(({data: {addOtherLeasePayment}}) => {
                                                                    push(addOtherLeasePayment.payments[0]);
                                                                    props.values.payments.push(addOtherLeasePayment.payments[0]);
                                                                    props.setSubmitting(false);
                                                                })
                                                                .catch((error) => console.log(error));
                                                        },
                                                        onRowUpdate: (newData, oldData) => {
                                                            const {
                                                                amount,
                                                                paymentType,
                                                                entity,
                                                                paymentFrom,
                                                                date,
                                                                id
                                                            } = newData;
                                                            return updateOtherLeasePayment({
                                                                variables: {
                                                                    paymentID: id,
                                                                    paymentInfo: {
                                                                        entity,
                                                                        paymentType,
                                                                        paymentFrom,
                                                                        date,
                                                                        amount: currencyFormat().format(amount.replace(/[^0-9.-]+/g, '')),
                                                                    },
                                                                },
                                                            }).then(({data: {updateOtherLeasePayment}}) => {
                                                                remove(oldData.tableData.id);
                                                                insert(oldData.tableData.id, updateOtherLeasePayment);
                                                            });
                                                        },
                                                        onRowDelete: (oldData) => {
                                                            props.setSubmitting(true);
                                                            return deleteOtherLeasePayment({
                                                                variables: {
                                                                    paymentID: oldData.id,
                                                                },
                                                            }).then((data) => {
                                                                props.setSubmitting(false);
                                                                remove(oldData.tableData.id);
                                                            });
                                                        },
                                                    }}
                                                />
                                            );
                                        }}
                                    </FieldArray>
                                </div>
                            </div>
                        </div>
                        <div>
                            <FieldArray name='operator.contacts'>
                                {() => {
                                    const columns = [
                                        {
                                            title: 'Current',
                                            field: 'current',
                                        },
                                        {
                                            title: 'Name',
                                            field: 'contactName',
                                            cellStyle: {
                                                whiteSpace: 'break-word',
                                                textAlign: 'left',
                                            },
                                        },
                                        {
                                            title: 'Position',
                                            field: 'position',
                                        },
                                        {
                                            title: 'Direct Line',
                                            field: 'directLine',
                                            cellStyle: {
                                                whiteSpace: 'nowrap',
                                                textAlign: 'left',
                                            },
                                        },
                                        {
                                            title: 'E-Mail',
                                            field: 'email',
                                        },
                                        {
                                            title: 'Cell Number',
                                            field: 'cell',
                                        },
                                        {
                                            title: 'Date',
                                            field: 'date',
                                            render: (rowData) =>
                                                !rowData.date || rowData.date === 'N/A'
                                                    ? 'N/A'
                                                    : moment(rowData.date).format('MM/DD/YYYY'),
                                            editComponent: (props) => (
                                                <MaterialUITextField type='date'
                                                                     onChange={(e) => props.onChange(e.target.value)}/>
                                            ),
                                            type: 'date',
                                        },
                                    ];
                                    return (
                                        <MaterialTable
                                            columns={columns}
                                            data={!props.values.operator ? [] : props.values.operator.contacts.map((c) => ({...c}))}
                                            title={
                                                !props.values.operator ? (
                                                    'Contacts'
                                                ) : (
                                                    <Link
                                                        to={`/Operators/${props.values.operator.id}?name=${encodeURIComponent(
                                                            props.values.operator.name,
                                                        )}`}
                                                        target='_blank'
                                                    >
                                                        {props.values.operator.name}
                                                    </Link>
                                                )
                                            }
                                            options={{
                                                headerStyle: {
                                                    backgroundColor: '#1976d2',
                                                    color: '#FFF',
                                                },
                                                rowStyle: (rowData) => ({
                                                    backgroundColor: rowData.current === 'Yes' ? '#EEE' : '#FFF',
                                                }),
                                                cellStyle: {
                                                    maxWidth: 0,
                                                    overflowX: 'auto',
                                                },
                                                paging: false,
                                                exportButton: true,
                                                toolbarButtonAlignment: 'left',
                                                searchFieldAlignment: 'left',
                                            }}
                                        />
                                    );
                                }}
                            </FieldArray>
                            <Field
                                name='comments'
                                placeholder='Notes'
                                label='Notes'
                                component={TextField}
                                multiline={true}
                                rows={4}
                                variant='outlined'
                                className={classes.notes}
                                fullWidth
                            />
                        </div>
                        <div>
                            <DeleteOtherLeaseModal
                                open={modalOpen}
                                handleClose={() => setModalOpen(false)}
                                handleAgree={() => {
                                    deleteOL({
                                        variables: {
                                            id: props.values.id,
                                        },
                                        update: (store, {data: {DeleteROW}}) => {
                                            store.data.delete(DeleteROW.id);
                                        },
                                    })
                                        .then(({data}) => {
                                            enqueueSnackbar('Successfully deleted Lease', {variant: 'success'});
                                            history.push('/OtherLeases');
                                        })
                                        .catch((err) => {
                                            console.error(err);
                                            enqueueSnackbar(err.toString(), {variant: 'error'});
                                        });
                                }}
                            />
                        </div>
                        <div className={classes.buttons}>
                            <Button variant='contained' color='primary' type='submit'>
                                Update
                            </Button>
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );
};
export default OtherLeaseDetailPage;